/**
 * SOLID Review
 * S — Strong. One concern: while a game is active, ask before the page unloads and intercept the browser's back button.
 * O — Good. A new unload-adjacent listener is one more line of its reaction.
 * L — Strong. A view model (`IServiceViewModel`) with no state of its own.
 * I — Strong. IPortPageUnloadGuard exposes one method, used by both listeners.
 * D — Strong. The port it is handed, the page's events and address roles.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { Reactions, type IConfigReaction } from './Reactions';
import type { IServicePageAddress, IServicePageEvents } from './platform/IConfigDepViewPlatform';

export interface IPropsSessionSnapshot {
  playerId: string;
  roomId: string;
  roomCode: string;
}

export interface IPortPageUnloadGuard {
  /** The game the tab is in, or null — the guard is active while it is not null. */
  getSession(): IPropsSessionSnapshot | null;
}

/** The history entry the back button consumes first. */
const SENTINEL: Readonly<{ gameGuard: true }> = Object.freeze({ gameGuard: true });
const NO_STATE: Readonly<Record<string, never>> = Object.freeze({});

/**
 * THE PAGE UNLOAD GUARD (frontend-framework-oo P2, 2026-10-29, §28.294) — `hooks/usePageUnloadGuard.ts`'s mount effect, now a
 * reaction started once per mount: a sentinel history entry the back button consumes first, the browser's "Leave site?" prompt
 * while a game is active, and the sentinel pushed again when the back button took it. The stored game is not written here (since
 * strict-ports FE2 the decision that changes the seat writes it).
 */
export class PageUnloadGuard implements IServiceViewModel<typeof NO_STATE> {
  private readonly declared: ReadonlyArray<IConfigReaction<null>> = [{
    deps: () => [],
    run: () => {
      this.address.push(SENTINEL);
      const leaving = this.events.on('beforeunload', (event) => {
        if (this.port.getSession()) {
          event.preventDefault();
          // Legacy support — modern browsers ignore returnValue but still show the dialog.
          (event as BeforeUnloadEvent).returnValue = '';
        }
      });
      const back = this.events.on('popstate', () => {
        // Re-push the sentinel so the next back press is also intercepted.
        if (this.port.getSession()) this.address.push(SENTINEL);
      });
      return () => {
        leaving();
        back();
      };
    },
  }];
  private readonly reactions = new Reactions<null>(this.declared);

  constructor(
    private readonly port: IPortPageUnloadGuard,
    private readonly address: IServicePageAddress,
    private readonly events: IServicePageEvents,
  ) {}

  /** Its reaction — started once per mount — in the list of the screen's model that composes it (AppRoot's, P3). */
  reactionsOver<TFacts>(): Array<IConfigReaction<TFacts>> {
    return Reactions.over(this.declared, () => null);
  }

  getSnapshot(): typeof NO_STATE {
    return NO_STATE;
  }

  subscribe(): () => void {
    return () => undefined;
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(): void {
    this.reactions.stopChanged(null);
  }

  commit(): void {
    this.reactions.startChanged(null);
  }
}
